import React, { useEffect, useState } from "react";
import { ActivityCard, ChoiceButton, MixedText, PlayButton, ProgressPill, useShuffledChoices } from "../shared";

const NO_CHOICES = [];

/**
 * DialoguePlay — a scripted two-person conversation the learner taps through.
 *
 * Lines reveal one at a time (with audio). Plain lines advance on "Continue";
 * lines flagged your_turn pause the script and make the learner pick the
 * right thing to say from data-driven choices — wrong picks shake and stay
 * retryable. The learner's speaker (data.you) renders on the right, chat
 * style. Completes when the whole script has played out.
 */
export default function DialoguePlay({ data, onComplete, audio }) {
  const [revealedCount, setRevealedCount] = useState(0);
  const [picked, setPicked] = useState(null); // wrong-choice index on a your_turn line

  const lines = data.lines;
  const done = revealedCount >= lines.length;
  const current = done ? null : lines[revealedCount];
  const view = useShuffledChoices(current?.choices || NO_CHOICES, current?.correct_index ?? 0, [current]);

  useEffect(() => {
    if (done) onComplete();
  }, [done, onComplete]);

  const reveal = (line) => {
    audio.play(line.ar);
    setPicked(null);
    setRevealedCount((n) => n + 1);
  };

  const handleChoice = (idx) => {
    if (!current) return;
    if (idx === view.correctIndex) {
      reveal(current);
    } else {
      setPicked(idx);
      setTimeout(() => setPicked(null), 900);
    }
  };

  const speakerOf = (line) => data.speakers[line.speaker] || {};
  const isYou = (line) => line.speaker === data.you;

  return (
    <ActivityCard>
      <div className="flex items-center justify-between gap-3 mb-4">
        <ProgressPill>{revealedCount}/{lines.length} lines</ProgressPill>
        <span className="text-sm" style={{ color: "var(--parchment-dim)" }}>
          You speak for{" "}
          <span dir="rtl" lang="ar" className="font-arabic" style={{ color: "var(--gold-bright)" }}>
            {data.speakers[data.you]?.name_ar}
          </span>
        </span>
      </div>

      <div className="flex flex-col gap-2.5 max-w-2xl mx-auto">
        {lines.slice(0, revealedCount).map((line) => (
          <div key={line.id} className={`flex ${isYou(line) ? "justify-end" : "justify-start"}`}>
            <div
              className="rounded-xl px-4 py-2.5 max-w-[85%]"
              style={{
                background: isYou(line) ? "var(--gold-bg)" : "var(--ink)",
                border: `1px solid ${isYou(line) ? "var(--gold-dim)" : "var(--panel-line)"}`,
              }}
            >
              <span dir="rtl" lang="ar" className="font-arabic text-xs block mb-0.5" style={{ color: "var(--parchment-dim)" }}>
                {speakerOf(line).name_ar}
              </span>
              <span className="flex items-center gap-2.5">
                <span dir="rtl" lang="ar" className="font-arabic text-2xl leading-[1.7]" style={{ color: "var(--parchment)" }}>
                  {line.ar}
                </span>
                {audio.audioAllowed && (
                  <PlayButton text={line.ar} playingText={audio.playingText} onPlay={audio.play} />
                )}
              </span>
              <span className="text-xs block mt-0.5" style={{ color: "var(--parchment-dim)" }}>{line.en}</span>
            </div>
          </div>
        ))}
      </div>

      {current && !current.your_turn && (
        <div className="flex justify-center mt-5">
          <button
            type="button"
            onClick={() => reveal(current)}
            className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 min-h-[44px] text-sm font-semibold rounded-lg transition-[filter] hover:brightness-105"
            style={{ background: "var(--gold)", color: "var(--ink)" }}
          >
            {revealedCount === 0 ? "Start the conversation" : "Continue"} →
          </button>
        </div>
      )}

      {current && current.your_turn && (
        <div className="mt-5 max-w-md mx-auto">
          <p className="text-sm text-center font-medium mb-3" style={{ color: "var(--gold-bright)" }}>
            Your turn — {current.prompt_en}
          </p>
          <div className="grid grid-cols-1 gap-2.5">
            {view.choices.map((choice, idx) => (
              <ChoiceButton
                key={`${current.id}-${idx}`}
                arabic
                state={picked === idx ? "wrong" : "idle"}
                disabled={picked !== null}
                onClick={() => handleChoice(idx)}
              >
                {choice}
              </ChoiceButton>
            ))}
          </div>
        </div>
      )}

      {done && (
        <div
          className="flex items-start gap-2.5 mt-5 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">💬</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.done_message} />
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
